import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { AttachmentViewer, type AttachmentViewerSubject } from '@/components/attachments/AttachmentViewer';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import {
  ATTACHMENT_ACCEPT,
  attachmentSubtitle,
  uploaderLabel,
} from '@/lib/company-attachments';
import { saveBlob } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { uploadTaskAttachmentFile, validateTaskAttachmentFile } from '@/lib/task-attachments';
import type { TaskAttachment } from '@/lib/types';

/*
 * The files and links kept against one task.
 *
 * Modelled on `CompanyAttachmentsPanel` and deliberately lighter: it renders
 * inside a task row and inside a modal, so it carries no `Panel` chrome of its
 * own — a small heading, the list, and the controls.
 *
 * ⚠️ **`canEdit` is decided by the CALLER** — owner's decision: anybody who may
 * edit the task may add and remove its attachments, including on a closed
 * task. This component never asks `ability` a question of its own, so the task
 * surface and this panel cannot disagree about who may write. The server's
 * refusal is still the guarantee: every write handles its own failure.
 *
 * ⚠️ **Reading is never gated here.** The list renders for every reader who can
 * see the task; `canEdit` withholds the CONTROLS, never the values.
 *
 * ⛔ **Size and type rules are not restated here** — they come from
 * `@/lib/company-attachments` through `@/lib/task-attachments`.
 */

/** The server's code for a bucket that is not configured (a 503). */
const STORAGE_NOT_CONFIGURED = 'OBJECT_STORAGE_NOT_CONFIGURED';

type ListState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; list: readonly TaskAttachment[] };

function messageOf(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? (caught.message || fallback);
  return caught instanceof Error && caught.message !== '' ? caught.message : fallback;
}

function isStorageUnavailable(caught: unknown): boolean {
  if (caught instanceof ApiError) {
    return caught.code === STORAGE_NOT_CONFIGURED || caught.status === 503;
  }
  /*
   * `uploadTaskAttachmentFile` wraps a step-3 failure in a plain Error and
   * keeps the original on `cause`, so a 503 from the record step still counts.
   */
  if (caught instanceof Error && caught.cause !== undefined) return isStorageUnavailable(caught.cause);
  return false;
}

function AttachmentRow({
  taskId,
  attachment,
  canEdit,
  onEdit,
  onDelete,
}: {
  taskId: number;
  attachment: TaskAttachment;
  canEdit: boolean;
  onEdit: (attachment: TaskAttachment) => void;
  onDelete: (attachment: TaskAttachment) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Built once on click and held here, so the viewer's load effect sees a stable subject. */
  const [viewing, setViewing] = useState<AttachmentViewerSubject | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const download = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const { filename, blob } = await api.downloadTaskAttachment(taskId, attachment.id);
      /* Never write a file to disk for a row the reader has navigated away from. */
      if (!mounted.current) return;
      saveBlob(filename, blob);
    } catch (caught) {
      if (!mounted.current) return;
      setError(messageOf(caught, 'The file couldn’t be downloaded.'));
    } finally {
      setDownloading(false);
    }
  }, [attachment.id, downloading, taskId]);

  return (
    <li className="border-b border-border py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 text-xs font-medium text-fg [overflow-wrap:anywhere]">
          {attachment.title}
        </span>
        {/* The kind decides the action — Download for a file, Open for a link. */}
        <Badge tone="neutral" variant="outline">{attachment.kind === 'file' ? 'File' : 'Link'}</Badge>
      </div>

      <p className="m-0 mt-0.5 text-2xs text-fg-muted [overflow-wrap:anywhere]">
        {[attachmentSubtitle(attachment), uploaderLabel(attachment.uploadedBy), formatDateTime(attachment.createdAt)]
          .filter((part) => part !== '')
          .join(' · ')}
      </p>

      {attachment.note !== null && attachment.note !== '' && (
        <p className="m-0 mt-1 text-xs whitespace-pre-line text-fg-secondary [overflow-wrap:anywhere]">
          {attachment.note}
        </p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {attachment.kind === 'file' ? (
          <>
            {/* Opens in the in-app viewer (owner's decision, 2026-09-25). */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setViewing({
                  kind: 'file',
                  title: attachment.title,
                  fileName: attachment.fileName,
                  contentType: attachment.contentType,
                  load: (signal) => api.downloadTaskAttachment(taskId, attachment.id, signal),
                })
              }
            >
              Open
            </Button>
            <Button variant="ghost" size="sm" loading={downloading} onClick={() => void download()}>
              Download
            </Button>
          </>
        ) : attachment.url !== null && attachment.url !== '' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setViewing({ kind: 'link', title: attachment.title, url: attachment.url ?? '' })}
          >
            Open
          </Button>
        ) : (
          <p className="m-0 text-xs text-fg-muted">This link has no address recorded.</p>
        )}
        {canEdit && (
          <>
            <Button variant="ghost" size="sm" onClick={() => onEdit(attachment)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(attachment)}>
              Remove
            </Button>
          </>
        )}
      </div>

      {error !== null && (
        <p role="alert" className="m-0 mt-1 text-xs text-danger [overflow-wrap:anywhere]">
          {error}
        </p>
      )}

      <AttachmentViewer subject={viewing} onClose={() => setViewing(null)} />
    </li>
  );
}

function EditDialog({
  taskId,
  attachment,
  onClose,
  onSaved,
}: {
  taskId: number;
  attachment: TaskAttachment;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(attachment.title);
  const [note, setNote] = useState(attachment.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const nextNote = note === '' ? null : note;
  /* An empty PATCH is a 422, so Save is held down until something moved. */
  const unchanged = title === attachment.title && nextNote === attachment.note;

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      /* Only what changed; an emptied note sends `null`, which is what clears it. */
      const patch: { title?: string; note?: string | null } = {};
      if (title !== attachment.title) patch.title = title;
      if (nextNote !== attachment.note) patch.note = nextNote;
      await api.updateTaskAttachment(taskId, attachment.id, patch);
      onSaved();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const { fields, unroutable } = caught.routeFieldErrors(['title', 'note']);
        setFieldErrors(fields);
        setError(
          unroutable.length > 0 ? unroutable.join(' ') : (caught.formMessage ?? caught.message),
        );
      } else {
        setError(messageOf(caught, 'The attachment couldn’t be saved.'));
      }
    } finally {
      setSaving(false);
    }
  }, [attachment.id, attachment.note, attachment.title, nextNote, onClose, onSaved, saving, taskId, title]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit attachment"
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} loading={saving} disabled={unchanged}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label="Title" required error={fieldErrors.title}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={title}
              invalid={fieldErrors.title !== undefined}
              disabled={saving}
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
        <Field label="Note" error={fieldErrors.note}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={note}
              invalid={fieldErrors.note !== undefined}
              disabled={saving}
              autoGrow={{ minRows: 2, maxRows: 6 }}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>
        {error !== null && (
          <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function LinkForm({
  taskId,
  onAdded,
  onCancel,
}: {
  taskId: number;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.createTaskAttachment(taskId, { kind: 'link', url, title });
      onAdded();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const { fields, unroutable } = caught.routeFieldErrors(['url', 'title']);
        setFieldErrors(fields);
        setError(
          unroutable.length > 0 ? unroutable.join(' ') : (caught.formMessage ?? caught.message),
        );
      } else {
        setError(messageOf(caught, 'The link couldn’t be added.'));
      }
    } finally {
      setSaving(false);
    }
  }, [onAdded, saving, taskId, title, url]);

  return (
    <form
      className="mt-2 grid gap-2 rounded-md border border-border bg-surface-sunken p-2"
      onSubmit={(event) => {
        event.preventDefault();
        /* A form inside a modal: its submit must not reach a form behind it. */
        event.stopPropagation();
        void submit();
      }}
    >
      <Field label="Address" required error={fieldErrors.url}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            type="url"
            value={url}
            invalid={fieldErrors.url !== undefined}
            disabled={saving}
            onChange={(event) => setUrl(event.target.value)}
          />
        )}
      </Field>
      <Field label="Title" required error={fieldErrors.title}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={title}
            invalid={fieldErrors.title !== undefined}
            disabled={saving}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>
      {error !== null && (
        <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Button type="submit" size="sm" loading={saving} disabled={url.trim() === ''}>
          Add link
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function TaskAttachmentsPanel({
  taskId,
  canEdit,
  onCountChange,
}: {
  taskId: number;
  canEdit: boolean;
  onCountChange?: (n: number) => void;
}) {
  const headingId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [nonce, setNonce] = useState(0);
  const [result, setResult] = useState<{ key: string; state: ListState } | null>(null);

  const [linkOpen, setLinkOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [storageUnavailable, setStorageUnavailable] = useState(false);

  const [editing, setEditing] = useState<TaskAttachment | null>(null);
  const [deleting, setDeleting] = useState<TaskAttachment | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const uploadAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => uploadAbortRef.current?.abort(), []);

  const key = `${taskId}:${nonce}`;
  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listTaskAttachments(taskId, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setResult({ key, state: { status: 'ready', list: answer.data } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          state: { status: 'error', message: messageOf(caught, 'The attachments couldn’t be loaded.') },
        });
      });
    return () => controller.abort();
  }, [key, taskId]);

  /* Loading is derived from the key, so a change of task never shows the last one's files. */
  const state: ListState = result?.key === key ? result.state : { status: 'loading' };
  const count = state.status === 'ready' ? state.list.length : null;

  /*
   * Only a SETTLED count is reported. A loading or failed read is not zero, and
   * a caller showing a badge must not be told "0" about a list nobody has read.
   *
   * ⚠️ The callback is held in a REF and the effect depends on `count` alone.
   * A caller passing an inline arrow hands a new function every render; with it
   * in the dependency list the effect re-fired every render, the caller set
   * state, re-rendered, and looped ("Maximum update depth exceeded" on /tasks).
   */
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  });
  useEffect(() => {
    if (count !== null) onCountChangeRef.current?.(count);
  }, [count]);

  const chooseFiles = useCallback(
    async (files: FileList | null) => {
      if (files === null || files.length === 0 || uploading) return;

      setUploadError(null);

      /* Every file is checked before any bytes are spent; refusals name the file. */
      const chosen = Array.from(files);
      const refusals = chosen
        .map((file) => validateTaskAttachmentFile(file))
        .filter((refusal): refusal is string => refusal !== null);
      if (refusals.length > 0) {
        setUploadError(refusals.join(' '));
        return;
      }

      const controller = new AbortController();
      uploadAbortRef.current?.abort();
      uploadAbortRef.current = controller;
      setUploading(true);

      let added = 0;
      try {
        for (const file of chosen) {
          await uploadTaskAttachmentFile(taskId, file, controller.signal);
          added += 1;
          if (controller.signal.aborted) return;
        }
      } catch (caught) {
        if (controller.signal.aborted) return;
        if (isStorageUnavailable(caught)) {
          setStorageUnavailable(true);
        } else {
          setUploadError(messageOf(caught, 'The file couldn’t be attached.'));
        }
      } finally {
        if (uploadAbortRef.current === controller) uploadAbortRef.current = null;
        if (!controller.signal.aborted) {
          setUploading(false);
          /* Re-read whenever anything landed, including the files before a failure. */
          if (added > 0) reload();
        }
      }
    },
    [reload, taskId, uploading],
  );

  const confirmDelete = useCallback(async () => {
    if (deleting === null || deletePending) return;
    setDeletePending(true);
    setDeleteError(null);
    try {
      await api.deleteTaskAttachment(taskId, deleting.id);
      setDeleting(null);
      reload();
    } catch (caught) {
      setDeleteError(messageOf(caught, 'The attachment couldn’t be removed.'));
    } finally {
      setDeletePending(false);
    }
  }, [deletePending, deleting, reload, taskId]);

  return (
    <section aria-labelledby={headingId} className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={headingId} className="m-0 text-xs font-semibold text-fg-secondary">
          Attachments{count !== null && count > 0 ? ` (${count})` : ''}
        </h3>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-1.5">
            {!storageUnavailable && (
              <>
                {/*
                  A native file input, hidden and driven by a Button: there is no
                  file primitive, and a visible native control does not survive a
                  row this narrow.
                */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(event) => {
                    const files = event.target.files;
                    void chooseFiles(files);
                    /* Cleared so choosing the same file again still fires. */
                    event.target.value = '';
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  loading={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload file
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLinkOpen((open) => !open)}
              aria-expanded={linkOpen}
            >
              Add link
            </Button>
          </div>
        )}
      </div>

      {canEdit && storageUnavailable && (
        <p role="status" className="m-0 mt-1 text-xs text-warning [overflow-wrap:anywhere]">
          File uploads are unavailable because file storage is not configured on this server. Links
          can still be added.
        </p>
      )}

      {uploadError !== null && (
        <p role="alert" className="m-0 mt-1 text-xs text-danger [overflow-wrap:anywhere]">
          {uploadError}
        </p>
      )}

      {canEdit && linkOpen && (
        <LinkForm
          taskId={taskId}
          onCancel={() => setLinkOpen(false)}
          onAdded={() => {
            setLinkOpen(false);
            reload();
          }}
        />
      )}

      <div aria-busy={state.status === 'loading' || undefined} className="mt-1">
        {state.status === 'loading' ? (
          <div>
            {Array.from({ length: 2 }, (_, row) => (
              <div key={row} className="border-b border-border py-2 last:border-b-0">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="mt-1.5 h-3 w-56" />
              </div>
            ))}
          </div>
        ) : state.status === 'error' ? (
          <Notice
            tone="danger"
            role="alert"
            title="Couldn’t load attachments."
            className="my-2"
            action={
              <Button size="sm" onClick={reload}>
                Try again
              </Button>
            }
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">{state.message}</span>
          </Notice>
        ) : state.list.length === 0 ? (
          <EmptyState
            className="py-3"
            title="Nothing attached yet"
            description={canEdit ? 'Upload a file or add a link.' : undefined}
          />
        ) : (
          <ul className="m-0 list-none p-0">
            {state.list.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                taskId={taskId}
                attachment={attachment}
                canEdit={canEdit}
                onEdit={setEditing}
                onDelete={(target) => {
                  setDeleteError(null);
                  setDeleting(target);
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {editing !== null && (
        <EditDialog
          key={editing.id}
          taskId={taskId}
          attachment={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}

      {deleting !== null && (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
          onConfirm={() => void confirmDelete()}
          title="Remove this attachment?"
          description={
            deleting.kind === 'file' ? (
              <>
                <strong className="font-semibold text-fg">{deleting.title}</strong> will be removed
                from this task, and the stored file is deleted with it. Neither can be recovered.
              </>
            ) : (
              <>
                <strong className="font-semibold text-fg">{deleting.title}</strong> will be removed
                from this task. The link itself is not affected, and the attachment cannot be
                recovered.
              </>
            )
          }
          confirmLabel="Remove"
          loading={deletePending}
          error={deleteError}
        />
      )}
    </section>
  );
}
